import { useState, useMemo } from "react";
import { AnimatePresence, motion, type PanInfo } from "framer-motion";
import { Check, Heart, X } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Eyebrow } from "@/components/editorial";
import { OnboardingIcon } from "@/components/onboarding-icons/OnboardingIcons";
import type { IconName } from "@/components/onboarding-icons/OnboardingIcons";
import { cn } from "@/lib/utils";
import {
  ALLERGY_DECK,
  APPETITE,
  DIETS,
  EQUIPMENT_OPTIONS,
  EXPERTISE,
  WEEKDAY_LABELS_SHORT,
  WEEKDAY_LABELS_LONG,
} from "./constants";
import { useOnboarding } from "./store";

// ─── Step 1: household size ───────────────────────────────────────────
export function StepSize() {
  const size = useOnboarding((s) => s.size);
  const setSize = useOnboarding((s) => s.setSize);
  return (
    <StepShell
      heading={
        <>
          Combien êtes-vous <span className="italic">à table</span> ?
        </>
      }
      subheading="On dimensionne les recettes et la liste de courses."
    >
      <div className="flex flex-col items-center gap-10">
        <div className="flex items-center justify-center text-ink">
          <OnboardingIcon
            name={`household_${Math.min(size, 4)}` as IconName}
            className="size-24"
          />
        </div>
        <div className="font-serif text-[120px] font-light leading-none tracking-[-0.03em] tabular-nums text-foreground">
          {size}
        </div>
        {/* Quick-adjust segmented control */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setSize(Math.max(1, size - 1))}
            aria-label="Moins une personne"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-hair text-lg hover:border-ink-30"
          >
            −
          </button>
          <button
            type="button"
            onClick={() => {
              if (size <= 4) setSize(size <= 2 ? 4 : 2);
              else setSize(size <= 6 ? 8 : 12);
            }}
            className={cn(
              "rounded-full border px-4 text-[12px] uppercase tracking-[0.08em] transition-colors",
              size <= 2
                ? "border-foreground bg-foreground text-background"
                : "border-hair text-ink-50 hover:border-ink-30",
            )}
          >
            Petit foyer
          </button>
          <button
            type="button"
            onClick={() => {
              if (size <= 4) setSize(size <= 2 ? 6 : 4);
              else setSize(size <= 6 ? 12 : 6);
            }}
            className={cn(
              "rounded-full border px-4 text-[12px] uppercase tracking-[0.08em] transition-colors",
              size >= 6
                ? "border-foreground bg-foreground text-background"
                : "border-hair text-ink-50 hover:border-ink-30",
            )}
          >
            Grande tablée
          </button>
          <button
            type="button"
            onClick={() => setSize(Math.min(12, size + 1))}
            aria-label="Plus une personne"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-hair text-lg hover:border-ink-30"
          >
            +
          </button>
        </div>
        <Slider
          value={useMemo(() => [size], [size])}
          min={1}
          max={12}
          step={1}
          onValueChange={(v) => setSize(v[0] ?? 1)}
          className="w-full max-w-sm"
          aria-label="Taille du foyer"
        />
        <p className="font-serif text-lg italic text-ink-50">
          {size === 1 ? "personne" : "personnes"}
        </p>
      </div>
    </StepShell>
  );
}

// ─── Step 2: diet ─────────────────────────────────────────────────────
export function StepDiet() {
  const diet = useOnboarding((s) => s.diet);
  const setDiet = useOnboarding((s) => s.setDiet);
  return (
    <StepShell
      heading={
        <>
          Quel est votre <span className="italic">régime</span> ?
        </>
      }
      subheading="Un seul choix — nous personnalisons les sept dîners en conséquence."
    >
      <div className="grid w-full max-w-md gap-2.5">
        {DIETS.map((d) => (
          <RowCard
            key={d.id}
            label={d.label}
            sub={d.hint}
            icon={d.icon}
            selected={diet === d.id}
            onClick={() => setDiet(d.id)}
          />
        ))}
      </div>
    </StepShell>
  );
}

// ─── Step 3: allergies (swipeable cards) ──────────────────────────────
export function StepAllergies() {
  const allergies = useOnboarding((s) => s.allergies);
  const toggle = useOnboarding((s) => s.toggleAllergy);
  const [index, setIndex] = useState(0);

  const current = ALLERGY_DECK[index];
  const remaining = ALLERGY_DECK.length - index;

  const exclude = () => {
    if (!current) return;
    if (!allergies.includes(current.id)) toggle(current.id);
    setIndex((i) => i + 1);
  };
  const skip = () => setIndex((i) => i + 1);

  return (
    <StepShell
      heading={
        <>
          Allergies ou <span className="italic">intolérances</span> ?
        </>
      }
      subheading="Glisser à droite pour exclure, à gauche pour ignorer."
    >
      <div className="flex w-full flex-col items-center gap-8">
        <div className="relative h-72 w-full max-w-sm">
          <AnimatePresence>
            {current ? (
              <SwipeCard
                key={current.id}
                label={current.label}
                icon={current.icon}
                excluded={allergies.includes(current.id)}
                onExclude={exclude}
                onSkip={skip}
              />
            ) : (
              <motion.div
                key="done"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid h-full w-full place-items-center border border-hair bg-paper p-6 text-center"
              >
                <div>
                  <Eyebrow accent className="mb-3 justify-center">
                    ✓ Terminé
                  </Eyebrow>
                  <p className="font-serif text-xl italic text-foreground">
                    {allergies.length === 0
                      ? "Aucune allergie sélectionnée."
                      : `${allergies.length} exclu(e)s.`}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {current && (
          <div className="flex items-center gap-6">
            <Button
              type="button"
              size="icon-lg"
              variant="outline"
              onClick={skip}
              aria-label="Ignorer"
            >
              <X className="size-5" />
            </Button>
            <p className="min-w-20 text-center text-[11px] uppercase tracking-[0.18em] text-ink-50">
              {remaining} {remaining === 1 ? "restante" : "restantes"}
            </p>
            <Button
              type="button"
              size="icon-lg"
              variant="accent"
              onClick={exclude}
              aria-label="Exclure"
            >
              <Heart className="size-5" />
            </Button>
          </div>
        )}
      </div>
    </StepShell>
  );
}

function SwipeCard({
  label,
  icon,
  excluded,
  onExclude,
  onSkip,
}: {
  label: string;
  icon: IconName;
  excluded: boolean;
  onExclude: () => void;
  onSkip: () => void;
}) {
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x > 120) onExclude();
    else if (info.offset.x < -120) onSkip();
  };
  return (
    <motion.div
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={handleDragEnd}
      initial={{ opacity: 0, scale: 0.96, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, x: 240, rotate: 12 }}
      whileTap={{ scale: 0.98 }}
      className={cn(
        "absolute inset-0 flex flex-col items-center justify-center border border-hair bg-background p-6 text-center shadow-soft-warm-lg",
        excluded && "border-accent ring-1 ring-accent",
      )}
    >
      <div className="flex items-center justify-center text-ink">
        <OnboardingIcon name={icon} className="size-20" />
      </div>
      <div className="mt-6 font-serif text-2xl font-normal tracking-[-0.005em]">
        {label}
      </div>
      <div className="mt-3 text-[10.5px] uppercase tracking-[0.18em] text-ink-50">
        {excluded ? "Déjà dans votre liste" : "← Ignorer  ·  Exclure →"}
      </div>
    </motion.div>
  );
}

// ─── Step 4: equipment ────────────────────────────────────────────────
export function StepEquipment() {
  const equipment = useOnboarding((s) => s.equipment);
  const toggle = useOnboarding((s) => s.toggleEquipment);
  return (
    <StepShell
      heading={
        <>
          Votre <span className="italic">matériel</span> ?
        </>
      }
      subheading="On adaptera les recettes à ce que vous avez."
    >
      <div className="flex w-full max-w-md flex-wrap gap-2">
        {EQUIPMENT_OPTIONS.map((e) => (
          <Chip
            key={e.id}
            label={e.label}
            icon={e.icon}
            selected={equipment.includes(e.id)}
            onClick={() => toggle(e.id)}
          />
        ))}
      </div>
    </StepShell>
  );
}

// ─── Step 5: expertise ────────────────────────────────────────────────
export function StepExpertise() {
  const expertise = useOnboarding((s) => s.expertise);
  const setExpertise = useOnboarding((s) => s.setExpertise);
  return (
    <StepShell
      heading={
        <>
          Niveau en <span className="italic">cuisine</span> ?
        </>
      }
      subheading="On calibre la complexité des recettes."
    >
      <div className="flex w-full max-w-md flex-col gap-2.5">
        {EXPERTISE.map((e) => (
          <RowCard
            key={e.id}
            label={e.label}
            sub={e.hint}
            icon={e.icon}
            selected={expertise === e.id}
            onClick={() => setExpertise(e.id)}
          />
        ))}
      </div>
    </StepShell>
  );
}

// ─── Step 6: appetite ─────────────────────────────────────────────────
export function StepAppetite() {
  const appetite = useOnboarding((s) => s.appetite);
  const setAppetite = useOnboarding((s) => s.setAppetite);
  return (
    <StepShell
      heading={
        <>
          Taille des <span className="italic">portions</span> ?
        </>
      }
      subheading="On calibre les apports caloriques de vos dîners."
    >
      <div className="flex w-full max-w-md flex-col gap-2.5">
        {APPETITE.map((a) => (
          <RowCard
            key={a.id}
            label={a.label}
            sub={a.hint}
            selected={appetite === a.id}
            onClick={() => setAppetite(a.id)}
          />
        ))}
      </div>
    </StepShell>
  );
}

// ─── Step: meat days ─────────────────────────────────────────────────────
export function StepMeatDays() {
  const diet = useOnboarding((s) => s.diet);
  const batchNumDays = useOnboarding((s) => s.batch_num_days ?? 7);
  const meatDays = useOnboarding((s) => s.meat_days_per_week);
  const setMeatDays = useOnboarding((s) => s.setMeatDaysPerWeek);

  const isPescatarian = diet === "pescatarian";
  const label = isPescatarian
    ? "Jours avec poisson / fruits de mer"
    : "Jours avec viande";
  const subLabel = isPescatarian
    ? "On planifiera des dîners végétariens pour le reste de la semaine."
    : "On planifiera des alternatives végétariennes pour les autres jours.";

  // null = unlimited; display as batchNumDays on the slider
  const sliderValue = meatDays ?? batchNumDays;

  return (
    <StepShell
      heading={
        <>
          Combien de jours{" "}
          <span className="italic">
            {isPescatarian ? "avec poisson" : "avec viande"}
          </span>{" "}
          ?
        </>
      }
      subheading={subLabel}
    >
      <div className="flex w-full max-w-md flex-col items-center gap-10">
        <div className="font-serif text-[100px] font-light leading-none tracking-[-0.03em] tabular-nums text-foreground">
          {meatDays === null ? "∞" : meatDays}
        </div>
        <p className="text-[13px] text-ink-50">
          {meatDays === null
            ? "Illimité"
            : `${meatDays} / ${batchNumDays} dîners`}
        </p>
        <Slider
          value={useMemo(() => [sliderValue], [sliderValue])}
          min={0}
          max={batchNumDays}
          step={1}
          onValueChange={(v) => {
            const val = v[0] ?? 0;
            setMeatDays(val === batchNumDays ? null : val);
          }}
          className="w-full max-w-sm"
          aria-label={label}
        />
        <p className="text-center text-[12px] text-ink-30">
          Glissez à gauche pour réduire · à droite pour illimité
        </p>
        <button
          type="button"
          className="text-[12px] text-ink-50 underline underline-offset-2"
          onClick={() => setMeatDays(null)}
        >
          Pas de limite
        </button>
      </div>
    </StepShell>
  );
}

// ─── Step: batch schedule ─────────────────────────────────────────────────
export function StepBatchSchedule() {
  const batchStartDay = useOnboarding((s) => s.batch_start_day ?? 0);
  const batchNumDays = useOnboarding((s) => s.batch_num_days ?? 7);
  const cookingDay = useOnboarding((s) => s.cooking_day ?? 6);
  const setBatchSchedule = useOnboarding((s) => s.setBatchSchedule);
  const setCookingDay = useOnboarding((s) => s.setCookingDay);

  // Compute the set of selected batch days from start + count
  const selectedDays = useMemo(
    () =>
      Array.from({ length: batchNumDays }, (_, i) => (batchStartDay + i) % 7),
    [batchStartDay, batchNumDays],
  );

  const handleDayToggle = (dayIdx: number) => {
    // Find the contiguous run that includes this day
    // Strategy: extend/contract the current [start, start+num-1] range
    const end = (batchStartDay + batchNumDays - 1) % 7;
    if (selectedDays.includes(dayIdx)) {
      // Contracting: if it's the start or end, shrink. Otherwise no-op (can't split)
      if (dayIdx === batchStartDay && batchNumDays > 1) {
        setBatchSchedule((batchStartDay + 1) % 7, batchNumDays - 1);
      } else if (dayIdx === end && batchNumDays > 1) {
        setBatchSchedule(batchStartDay, batchNumDays - 1);
      }
    } else {
      // Extending: if adjacent to start or end, expand
      const prevEnd = (dayIdx - 1 + 7) % 7;
      const nextStart = (dayIdx + 1) % 7;
      if (prevEnd === end && batchNumDays < 7) {
        setBatchSchedule(batchStartDay, batchNumDays + 1);
      } else if (nextStart === batchStartDay && batchNumDays < 7) {
        setBatchSchedule(dayIdx, batchNumDays + 1);
      } else {
        // Not adjacent — start a new single-day selection
        setBatchSchedule(dayIdx, 1);
      }
    }
  };

  return (
    <StepShell
      heading={
        <>
          Quels jours <span className="italic">mangez-vous</span> batch ?
        </>
      }
      subheading="Sélectionnez les jours consécutifs où vous souhaitez des repas préparés."
    >
      <div className="flex w-full max-w-md flex-col gap-8">
        {/* Day picker */}
        <div>
          <p className="mb-3 text-[11px] uppercase tracking-[0.18em] text-ink-50">
            Jours batch
          </p>
          <div className="flex gap-2">
            {WEEKDAY_LABELS_SHORT.map((label, idx) => {
              const isSelected = selectedDays.includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleDayToggle(idx)}
                  aria-pressed={isSelected}
                  className={cn(
                    "flex flex-1 flex-col items-center rounded-sm border py-3 transition-colors",
                    isSelected
                      ? "border-foreground bg-foreground text-background"
                      : "border-hair bg-background text-foreground hover:border-ink-30",
                  )}
                >
                  <span className="text-[10px] uppercase tracking-[0.15em]">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-center text-[12px] text-ink-50">
            {batchNumDays} dîner{batchNumDays > 1 ? "s" : ""} / semaine
          </p>
        </div>

        {/* Cooking day picker */}
        <div>
          <p className="mb-3 text-[11px] uppercase tracking-[0.18em] text-ink-50">
            Jour de cuisson
          </p>
          <div className="flex gap-2">
            {WEEKDAY_LABELS_SHORT.map((label, idx) => {
              const isSelected = cookingDay === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCookingDay(idx)}
                  aria-pressed={isSelected}
                  className={cn(
                    "flex flex-1 flex-col items-center rounded-sm border py-3 transition-colors",
                    isSelected
                      ? "border-accent bg-accent/10 text-accent"
                      : "border-hair bg-background text-foreground hover:border-ink-30",
                  )}
                >
                  <span className="text-[10px] uppercase tracking-[0.15em]">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-center text-[12px] text-ink-50">
            Vous cuisinez le {WEEKDAY_LABELS_LONG[cookingDay]}
          </p>
        </div>
      </div>
    </StepShell>
  );
}

// ─── Step 7: food exclusions (chip/tag input) ──────────────────────────
export function StepFoodExclusions() {
  const exclusions = useOnboarding((s) => s.foodExclusions);
  const add = useOnboarding((s) => s.addFoodExclusion);
  const remove = useOnboarding((s) => s.removeFoodExclusion);
  const [input, setInput] = useState("");

  const handleAdd = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (exclusions.length >= 50) return;
    if (exclusions.includes(trimmed)) return;
    add(trimmed);
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <StepShell
      heading={
        <>
          Des aliments à <span className="italic">exclure</span> ?
        </>
      }
      subheading="Saisissez les ingrédients que vous ne voulez pas voir dans vos recettes."
    >
      <div className="w-full max-w-md space-y-5">
        {/* Input row */}
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="ex. brocoli, chocolat, champignons…"
            className="min-w-0 flex-1 border border-hair bg-background px-4 py-3 font-serif text-[15px] text-foreground outline-none placeholder:text-ink-30 focus:border-foreground"
            maxLength={100}
            autoComplete="off"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={!input.trim() || exclusions.length >= 50}
            className="flex shrink-0 items-center gap-1.5 border border-foreground bg-foreground px-4 py-3 text-[12.5px] font-medium text-background transition-opacity disabled:opacity-30"
          >
            <Check className="size-3.5" />
            Ajouter
          </button>
        </div>

        {/* Chip list */}
        {exclusions.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {exclusions.map((item) => (
              <span
                key={item}
                className="inline-flex items-center gap-1.5 border border-hair bg-background px-3 py-2 text-[12.5px] font-medium text-ink-70"
              >
                {item}
                <button
                  type="button"
                  onClick={() => remove(item)}
                  aria-label={`Retirer ${item}`}
                  className="flex size-4 items-center justify-center text-ink-30 hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-center font-serif text-[15px] italic text-ink-50">
            Aucun aliment exclu pour l'instant.
          </p>
        )}

        {/* Counter */}
        <p className="text-right text-[11px] text-ink-30">
          {exclusions.length} / 50
        </p>
      </div>
    </StepShell>
  );
}

// ─── Step 7: review ───────────────────────────────────────────────────
export function StepReview({
  isSubmitting,
  onSubmit,
}: {
  isSubmitting: boolean;
  onSubmit: () => void;
}) {
  const profile = useOnboarding();
  const dietLabel =
    DIETS.find((d) => d.id === profile.diet)?.label ?? profile.diet;
  const expertiseLabel =
    EXPERTISE.find((e) => e.id === profile.expertise)?.label ??
    profile.expertise;
  const appetiteLabel =
    APPETITE.find((a) => a.id === profile.appetite)?.label ?? profile.appetite;
  return (
    <StepShell
      heading={
        <>
          Tout est <span className="italic">prêt</span>.
        </>
      }
      subheading="Vérifiez et lancez la composition de votre semaine."
    >
      <div className="w-full max-w-md">
        <dl className="border-y border-hair">
          <ReviewRow
            label="Foyer"
            value={`${profile.size} personne${profile.size > 1 ? "s" : ""}`}
          />
          <ReviewRow label="Régime" value={dietLabel} />
          <ReviewRow
            label="Allergies"
            value={
              profile.allergies.length > 0
                ? profile.allergies.join(", ")
                : "Aucune"
            }
          />
          <ReviewRow
            label="Matériel"
            value={
              profile.equipment.length > 0 ? profile.equipment.join(", ") : "—"
            }
          />
          <ReviewRow
            label="Aliments exclus"
            value={
              profile.foodExclusions.length > 0
                ? profile.foodExclusions.join(", ")
                : "Aucun"
            }
          />
          <ReviewRow label="Niveau" value={expertiseLabel} />
          <ReviewRow label="Portions" value={appetiteLabel} last />
        </dl>

        <Button
          size="xl"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="mt-10 w-full"
        >
          {isSubmitting ? "Enregistrement…" : "Composer mon menu"}
          <Check className="ml-1" />
        </Button>
      </div>
    </StepShell>
  );
}

// ─── Building blocks ──────────────────────────────────────────────────

function StepShell({
  heading,
  subheading,
  children,
}: {
  heading: React.ReactNode;
  subheading?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-start gap-10 text-left">
      <header className="space-y-3">
        <h1 className="h-display text-[40px] leading-[1.05] sm:text-[48px]">
          {heading}
        </h1>
        {subheading && (
          <p className="font-serif text-lg font-light leading-snug text-ink-70">
            {subheading}
          </p>
        )}
      </header>
      <div className="flex w-full flex-col items-center">{children}</div>
    </div>
  );
}

function RowCard({
  label,
  sub,
  icon,
  selected,
  onClick,
}: {
  label: string;
  sub?: string;
  icon?: IconName;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "flex w-full items-center gap-4 border bg-background px-5 py-4 text-left transition-colors",
        selected ? "border-foreground" : "border-hair hover:border-ink-30",
      )}
    >
      {icon ? (
        <div className="flex size-10 shrink-0 items-center justify-center text-ink">
          <OnboardingIcon name={icon} className="size-8" />
        </div>
      ) : (
        <span
          className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
            selected ? "border-foreground" : "border-ink-30",
          )}
        >
          {selected && <span className="h-2 w-2 rounded-full bg-foreground" />}
        </span>
      )}
      <span className="flex-1">
        <span className="block font-serif text-lg font-medium leading-tight tracking-[-0.005em]">
          {label}
        </span>
        {sub && (
          <span className="mt-0.5 block text-[12px] text-ink-50">{sub}</span>
        )}
      </span>
    </button>
  );
}

function Chip({
  label,
  icon,
  selected,
  onClick,
}: {
  label: string;
  icon?: IconName;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[12.5px] font-medium transition-colors",
        selected
          ? "border-foreground bg-foreground text-background"
          : "border-hair bg-background text-ink-70 hover:border-ink-30",
      )}
    >
      {icon && (
        <OnboardingIcon
          name={icon}
          className={cn("size-5", selected ? "text-background" : "text-ink")}
        />
      )}
      {selected && <Check className="size-3" />}
      {label}
    </button>
  );
}

function ReviewRow({
  label,
  value,
  last = false,
}: {
  label: string;
  value: string;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-4 py-4",
        !last && "border-b border-hair",
      )}
    >
      <dt className="text-[11px] uppercase tracking-[0.2em] text-ink-50">
        {label}
      </dt>
      <dd className="text-right font-serif text-lg text-foreground">{value}</dd>
    </div>
  );
}
